import { supabase } from '../lib/supabase.js';

class EmailService {
  /**
   * Send booking confirmation email
   */
  async sendBookingConfirmation(booking, event, attendee) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'booking_confirmation',
          recipientEmail: attendee?.email,
          recipientName: attendee?.full_name || 'Event Attendee',
          data: {
            eventTitle: event?.title,
            eventDate: this.formatDateTime(event?.event_date),
            eventLocation: event?.location,
            ticketType: booking?.ticket_type || 'Standard',
            ticketQuantity: booking?.quantity || 1,
            bookingReference: booking?.booking_reference,
            totalAmount: booking?.total_amount
          }
        }
      });

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error sending booking confirmation:', error);
      throw new Error(`Failed to send booking confirmation: ${error.message}`);
    }
  }

  /**
   * Send event reminder email
   */
  async sendEventReminder(booking, event, attendee) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'event_reminder',
          recipientEmail: attendee?.email,
          recipientName: attendee?.full_name || 'Event Attendee',
          data: {
            eventTitle: event?.title,
            eventDate: this.formatDateTime(event?.event_date),
            eventLocation: event?.location,
            ticketType: booking?.ticket_type || 'Standard',
            bookingReference: booking?.booking_reference
          }
        }
      });

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error sending event reminder:', error);
      throw new Error(`Failed to send event reminder: ${error.message}`);
    }
  }

  /**
   * Send welcome email to new users (Enhanced for signup flow)
   */
  async sendWelcomeEmail(userProfile) {
    try {
      console.log('🚀 Attempting to send welcome email to:', userProfile?.email);
      
      // ✅ Enhanced validation before making the request
      if (!userProfile?.email) {
        throw new Error('Email address is required');
      }

      // ✅ Test edge function availability first
      console.log('🔍 Testing edge function availability...');
      try {
        const testResponse = await supabase?.functions?.invoke('send-email', {
          method: 'GET'
        });
        
        if (testResponse?.error) {
          console.warn('⚠️ Edge function test failed:', testResponse?.error);
        } else {
          console.log('✅ Edge function test successful:', testResponse?.data);
        }
      } catch (testError) {
        console.warn('⚠️ Edge function test error:', testError?.message);
      }

      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'welcome_email',
          recipientEmail: userProfile?.email,
          recipientName: userProfile?.full_name || 'New User',
          data: {
            userRole: userProfile?.role || 'attendee'
          }
        }
      });

      if (error) {
        console.error('❌ Supabase function error:', error);
        throw error;
      }

      console.log('✅ Welcome email sent successfully:', data);
      return { success: true, data };
    } catch (error) {
      console.error('💥 Error sending welcome email:', error);
      
      // ✅ Enhanced error handling with specific messages and user-friendly responses
      let userFriendlyMessage = 'Failed to send welcome email';
      
      if (error?.message?.includes('RESEND_API_KEY')) {
        userFriendlyMessage = 'Email service not configured. Please ensure RESEND_API_KEY is set in Supabase project settings.';
      } else if (error?.message?.includes('Function not found') || error?.message?.includes('send-email')) {
        userFriendlyMessage = 'Email edge function not deployed. Please deploy the send-email edge function to Supabase.';
      } else if (error?.message?.includes('non-2xx') || error?.message?.includes('Edge Function returned')) {
        userFriendlyMessage = 'Email service temporarily unavailable. Please try again later.';
      } else if (error?.message?.includes('Invalid email') || error?.message?.includes('email format')) {
        userFriendlyMessage = 'Invalid email address provided.';
      } else if (error?.message?.includes('timeout') || error?.message?.includes('Request timeout')) {
        userFriendlyMessage = 'Email service timeout. Please try again in a moment.';
      } else if (error?.message?.includes('Rate limit')) {
        userFriendlyMessage = 'Too many email requests. Please wait a moment and try again.';
      } else if (error?.message?.includes('Email address is required')) {
        userFriendlyMessage = 'Email address is missing from user profile.';
      } else if (error?.message?.includes('Invalid API key') || error?.message?.includes('401')) {
        userFriendlyMessage = 'Email service authentication failed. Please check the RESEND_API_KEY configuration.';
      }
      
      // ✅ Always throw with user-friendly message but preserve original error details
      const enhancedError = new Error(`${userFriendlyMessage}: ${error.message}`);
      enhancedError.originalError = error;
      enhancedError.isEmailServiceError = true;
      enhancedError.userFriendlyMessage = userFriendlyMessage;
      
      throw enhancedError;
    }
  }

  /**
   * Send bulk welcome emails to existing users
   */
  async sendBulkWelcomeEmails() {
    try {
      // Get all active users
      const { data: users, error: usersError } = await supabase?.from('user_profiles')?.select(`
          email,
          full_name,
          role
        `)?.eq('is_active', true);

      if (usersError) {
        throw usersError;
      }

      const emailPromises = users?.map(user => 
        this.sendWelcomeEmail(user)
      );

      const results = await Promise.allSettled(emailPromises);
      
      const successful = results?.filter(result => result?.status === 'fulfilled')?.length;
      const failed = results?.filter(result => result?.status === 'rejected')?.length;

      return {
        success: true,
        sent: successful,
        failed: failed,
        total: users?.length || 0
      };

    } catch (error) {
      console.error('Error sending bulk welcome emails:', error);
      throw new Error(`Failed to send bulk welcome emails: ${error.message}`);
    }
  }

  /**
   * Send staff assignment notification
   */
  async sendStaffAssignment(staff, event, assignment) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'staff_assignment',
          recipientEmail: staff?.email,
          recipientName: staff?.full_name || 'Staff Member',
          data: {
            eventTitle: event?.title,
            eventDate: this.formatDateTime(event?.event_date),
            eventLocation: event?.location,
            staffRole: assignment?.staff_role || 'Staff',
            shiftTime: this.formatShift(assignment?.shift_start, assignment?.shift_end)
          }
        }
      });

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error sending staff assignment:', error);
      throw new Error(`Failed to send staff assignment: ${error.message}`);
    }
  }

  /**
   * Send event cancellation notification
   */
  async sendEventCancellation(booking, event, attendee) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'event_cancelled',
          recipientEmail: attendee?.email,
          recipientName: attendee?.full_name || 'Event Attendee',
          data: {
            eventTitle: event?.title,
            eventDate: this.formatDateTime(event?.event_date),
            bookingReference: booking?.booking_reference,
            totalAmount: booking?.total_amount
          }
        }
      });

      if (error) {
        throw error;
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error sending event cancellation:', error);
      throw new Error(`Failed to send event cancellation: ${error.message}`);
    }
  }

  /**
   * Send bulk email reminders for an event
   */
  async sendBulkEventReminders(eventId) {
    try {
      // Get all bookings for the event with attendee details
      const { data: bookings, error: bookingsError } = await supabase?.from('bookings')?.select(`
          *,
          events(*),
          attendee:user_profiles!bookings_attendee_id_fkey(*)
        `)?.eq('event_id', eventId);

      if (bookingsError) {
        throw bookingsError;
      }

      const emailPromises = bookings?.map(booking => 
        this.sendEventReminder(booking, booking?.events, booking?.attendee)
      );

      const results = await Promise.allSettled(emailPromises);
      
      const successful = results?.filter(result => result?.status === 'fulfilled')?.length;
      const failed = results?.filter(result => result?.status === 'rejected')?.length;

      return {
        success: true,
        sent: successful,
        failed: failed,
        total: bookings?.length || 0
      };

    } catch (error) {
      console.error('Error sending bulk event reminders:', error);
      throw new Error(`Failed to send bulk reminders: ${error.message}`);
    }
  }

  /**
   * Format date and time for emails
   */
  formatDateTime(dateString) {
    if (!dateString) return 'Date TBA';
    
    try {
      const date = new Date(dateString);
      return date?.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Format shift time for staff assignments
   */
  formatShift(startTime, endTime) {
    if (!startTime || !endTime) return 'Full event duration';
    
    try {
      const start = new Date(startTime)?.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const end = new Date(endTime)?.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      return `${start} - ${end}`;
    } catch (error) {
      return `${startTime} - ${endTime}`;
    }
  }

  /**
   * Test email service configuration
   */
  async testEmailConfiguration() {
    try {
      console.log('🔧 Testing email service configuration...');
      
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        method: 'GET'
      });
      
      if (error) {
        throw error;
      }
      
      console.log('✅ Email service configuration test:', data);
      return { success: true, data };
    } catch (error) {
      console.error('❌ Email service configuration test failed:', error);
      return { 
        success: false, 
        error: error?.message || 'Configuration test failed',
        details: error 
      };
    }
  }
}

// Create and export singleton instance
export const emailService = new EmailService();
export default emailService;